import { ProportionalityRuleEnum } from '@/Network/Types/Requests/ScheduleRequests'
import type { CreateFullScheduleRequest } from '@/Network/Types/Requests/ScheduleRequests'
import type {
  AntecipeRuleChoice,
  LoadedAntecipeRule
} from '@/components/ManageScale/Schedule/antecipeRule'
import { defineStore } from 'pinia'
import Network from '@/Network'
import type {
  ImportedAttendancePolicy,
  ImportedPaymentPolicy,
  NamedConfigItem
} from '@/Network/Types/Responses/ScheduleUnitResponses'
import type { DSAttendancePolicyData, DSAttendancePolicyRule } from '@/types/ds-attendance.types'
import {
  CheckMethod,
  DSAttendanceDeadlineCalculationType,
  DSAttendanceMonitoringType,
  DSAttendanceRuleRecurrence
} from '@/constants/ds-attendance.constants'
import type { ApprovalGroup } from '@/types/approval-flow.types'
import {
  apiLayersToGroup,
  createEmptyGroup,
  ensureMandatoryRoles,
  groupToApiLayers
} from '@/utils/approval-flow-transformers'
import { toDateOnly, weekDays } from '@/utils/date'
import { getApiErrorMessage } from '@/utils/apiError'
import { buildAuditDeadlineSettings } from '@/components/ManageScale/Schedule/auditDeadline'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import {
  clonePolicy,
  emptyPolicy,
  isPolicyEmpty
} from '@/components/QualificationRequirement/policyModel'
import type { CreateScheduleQualificationPolicy } from '@/types/qualification-policy.types'
import type { ScheduleAgendaTypeValue } from '@/Network/Types/Responses/DemandResponses'
import { usePermissionsStore } from '@/pinia-store/usePermissionsStore'
import { AuthorizationPermissions, AuthorizationResourcesAndSubjects } from '@/types/authorization'
import {
  createDefaultTeamHandoverConfig,
  buildTeamHandoverPayload,
  mapToTeamHandoverConfig,
  type TeamHandoverConfig
} from '@/constants/handover.constants'

// Step 1 (Informações Básicas)
type ScheduleStep1 = {
  scheduleName: string
  timezone: string
  endDate: Date | null
  healthUnitId: number | null
  healthUnitName?: string
  exchangeDeadline: string
  proportionality: string
  operationalGroup: string | null
  teamDoctorReminderEnabled: boolean
  externalDoctorReminderEnabled: boolean
  /** Tipo de agenda (Demanda). Só vai no payload com a flag `enabledDashboardDemand`. */
  agendaType: ScheduleAgendaTypeValue
}

export interface Item {
  title: string
  value: number
}

export interface ItemTeams {
  title: string
  value: string
}

/**
 * Catálogo de times das regras de pagamento. Fonte única: o step de pagamento
 * popula `step2.teams` com estas opções e o import as recasa a partir dos
 * `isOurTeamValues` que vêm da política de origem.
 */
/**
 * Prefixo do nome ao duplicar uma escala via "Importar configurações". Mesmo padrão
 * dos endpoints de `duplicate` de política no backend (`Cópia de ${original.name}`).
 */
export const IMPORTED_NAME_PREFIX = 'Cópia de '

/**
 * Fallbacks de domínio do wizard de criação. Ficam colocalizados aqui (e não no
 * catálogo de genéricos) e são exportados para os callers usarem a MESMA
 * mensagem no `notifyError`, sem string duplicada divergindo com o tempo.
 */
export const CREATE_SCHEDULE_ERROR = 'Não foi possível criar a escala.'
export const IMPORT_CONFIGURATION_ERROR =
  'Não foi possível importar as configurações da escala selecionada.'
export const SCHEDULE_LOAD_ERROR = 'Não foi possível carregar os dados da escala.'

export const PAYMENT_TEAM_OPTIONS: ItemTeams[] = [
  { title: 'Interno', value: 'INTERNAL_TEAM' },
  { title: 'Externo', value: 'EXTERNAL_TEAM' }
]

// Step 2 (Requisitos Báscios)
export type ScheduleStep2 = {
  positions: Item[]
  shifts: Item[]
  formationRequired: boolean
  formations: Item[]
  teams: ItemTeams[]
  days: Item[]
}

// Step Excepcionalidade (Fluxo de Aprovação)
export type ApprovalFlowStep = {
  enabled: boolean
  layers: ApprovalGroup[]
}

// Step Exchange Config (Configurações de Trocas) — config por time
export interface ScheduleExchangeConfigStep {
  ourTeam: TeamHandoverConfig
  notOurTeam: TeamHandoverConfig
}

// Step 3 (Presença Médica) usa a entidade global DSAttendancePolicyData

// Step 4 (Pagamentos)
export interface ScheduleStep4 {
  name: string
  startDate: Date | null
  endDate: Date | null
  indefiniteEndDate: boolean
  rules: PaymentRule[]
}

export interface PaymentRule {
  days: Item[]
  shifts: Item[]
  positions: Item[]
  teams: ItemTeams[]
  formations: Item[]
  repassMode: 'FLOW' | 'IMMEDIATE'
  dutyValue: string
  /** Frente A / Q8 — ausente = padrão (Proporcional com limite). */
  antecipeRule?: AntecipeRuleChoice
  /** Valor do GET da política; presente = backend conhece o campo (preserva no update). */
  loadedAntecipeRule?: LoadedAntecipeRule
}

export const useCreateScheduleStore = defineStore('create-schedule-v2', {
  state: () => ({
    // Step 1: Informações Básicas
    step1: {
      scheduleName: '',
      timezone: '',
      endDate: null,
      healthUnitId: null,
      healthUnitName: '',
      exchangeDeadline: '',
      proportionality: ProportionalityRuleEnum.PROPORTIONAL_WITH_LIMIT,
      operationalGroup: null,
      teamDoctorReminderEnabled: true,
      externalDoctorReminderEnabled: true,
      agendaType: 'AUTO'
    } as ScheduleStep1,

    // Step Exchange Config: Configurações de Trocas (por time)
    // Defaults novos: Interno = "Apenas trocas aprovadas pelo painel web"; Externo = "Desabilitar".
    stepExchangeConfig: {
      ourTeam: createDefaultTeamHandoverConfig({
        allowDoctorStationHandover: true,
        requiresAdminApproval: true
      }),
      notOurTeam: createDefaultTeamHandoverConfig({
        allowDoctorStationHandover: false,
        requiresAdminApproval: true
      })
    } as ScheduleExchangeConfigStep,

    // Step 2: Requisitos Básicos
    step2: {
      positions: [],
      shifts: [],
      formations: [],
      teams: [],
      days: [],
      formationRequired: false
    } as ScheduleStep2,

    // Step 3: Presença Médica (Attendance Policy)
    step3: {
      name: '',
      startDate: new Date(),
      endDate: null,
      isIndeterminate: false,
      monitoringType: DSAttendanceMonitoringType.MINHA_ESCALA_APP,
      // M7 — `null` significa "ninguém decidiu ainda": é o que faz o passo 3 do wizard
      // HERDAR o estado de check em vez de nascer desligado (ver DsAttendancePolicy.vue,
      // `shouldInheritScheduleCheck`). O payload continua mandando boolean (`Boolean(...)`).
      requiredCheckOurTeam: null,
      requiredCheckNotOurTeam: null,
      checkMethodOurTeam: null,
      checkMethodNotOurTeam: null,
      submissionDeadlineHours: null,
      deadlineCalculationType: null,
      auditDeadlineHours: null,
      auditDeadlineCalculationType: null,
      checkinLateToleranceMinutes: null,
      checkoutEarlyToleranceMinutes: null,
      autoPermanenceOurTeam: true,
      autoPermanenceNotOurTeam: true,
      rules: [{ name: '', shiftIds: [], recurrence: null, dayOfMonth: null, daysOfWeek: [] }], // ← shift: []
      templates: []
    } as DSAttendancePolicyData,

    // Step Excepcionalidade: Fluxo de Aprovação
    stepApprovalFlow: {
      enabled: false,
      layers: [createEmptyGroup()]
    } as ApprovalFlowStep,

    // Step 4: Política de Pagamentos
    step4: {
      name: 'Nova política de pagamento',
      startDate: null,
      endDate: null,
      indefiniteEndDate: false,
      rules: [
        {
          days: [],
          shifts: [],
          positions: [],
          teams: [],
          formations: [],
          repassMode: 'FLOW',
          dutyValue: ''
        }
      ]
    } as ScheduleStep4,

    /**
     * Políticas de qualificação a replicar (RN-16), preenchidas por
     * `importFromSchedule`. Guarda o ID da política existente — não uma cópia:
     * política de qualificação é de empresa e compartilhada entre escalas.
     *
     * Não tem step no wizard: não há nada para o usuário preencher, só um
     * vínculo a criar depois que a escala existe.
     */
    stepQualification: {
      documental: null as NamedConfigItem | null,
      transfer: null as NamedConfigItem | null
    },

    /**
     * Qualificação exigida da escala nova (medical-docs-qualification). Viaja no full-create
     * (mesma transação), sem depender da permissão da escala recém-criada.
     */
    stepQualificationRequirement: { policy: emptyPolicy(), isValid: true },

    isDirty: false,

    /**
     * Estado de erro canônico da store (rule `error-handling.md`): a MENSAGEM
     * exibível da última falha de leitura/escrita, ou `null`. `hasError` deriva
     * dela — sem flags booleanas soltas. As actions continuam relançando, então
     * o caller pode escolher entre o toast (`notifyError`) e o estado visível.
     */
    loadError: null as string | null,
    isLoading: false,
    isSaving: false
  }),

  getters: {
    hasError: (state): boolean => state.loadError !== null
  },

  actions: {
    clearError() {
      this.loadError = null
    },
    reset() {
      this.$reset()
    },
    resetStepExchangeConfig() {
      this.stepExchangeConfig = {
        ourTeam: createDefaultTeamHandoverConfig({
          allowDoctorStationHandover: true,
          requiresAdminApproval: true
        }),
        notOurTeam: createDefaultTeamHandoverConfig({
          allowDoctorStationHandover: false,
          requiresAdminApproval: true
        })
      } as ScheduleExchangeConfigStep
    },
    resetStep2() {
      this.step2 = {
        positions: [],
        shifts: [],
        formations: [],
        teams: [],
        days: [],
        formationRequired: false
      } as ScheduleStep2
    },
    resetStep3() {
      this.step3 = {
        name: '',
        startDate: new Date(),
        endDate: null,
        isIndeterminate: false,
        monitoringType: DSAttendanceMonitoringType.MINHA_ESCALA_APP,
        // M7 — ver comentário no state inicial: `null` = herdar.
        requiredCheckOurTeam: null,
        requiredCheckNotOurTeam: null,
        checkMethodOurTeam: null,
        checkMethodNotOurTeam: null,
        checkinLateToleranceMinutes: null,
        checkoutEarlyToleranceMinutes: null,
        autoPermanenceOurTeam: true,
        autoPermanenceNotOurTeam: true,
        submissionDeadlineHours: null,
        deadlineCalculationType: null,
        auditDeadlineHours: null,
        auditDeadlineCalculationType: null,
        rules: [{ name: '', shiftIds: [], recurrence: null, dayOfMonth: null, daysOfWeek: [] }], // ← shift: []
        templates: []
      } as DSAttendancePolicyData
    },
    resetStepApprovalFlow() {
      this.stepApprovalFlow = {
        enabled: false,
        layers: [createEmptyGroup()]
      } as ApprovalFlowStep
    },
    resetStep4() {
      this.step4 = {
        name: 'Nova política de pagamento',
        startDate: null,
        endDate: null,
        indefiniteEndDate: false,
        rules: [
          {
            days: [],
            shifts: [],
            positions: [],
            teams: [],
            formations: [],
            repassMode: 'FLOW',
            dutyValue: ''
          }
        ]
      } as ScheduleStep4
    },
    markAsDirty() {
      this.isDirty = true
    },
    markAsClean() {
      this.isDirty = false
    },
    setPolicyName(newName: string) {
      this.step4.name = newName.trim()
    },
    addRule() {
      this.step4.rules.push({
        days: [],
        shifts: [],
        positions: [],
        teams: [],
        formations: [],
        repassMode: 'FLOW',
        dutyValue: ''
      })
    },
    removeRule(index: number) {
      this.step4.rules.splice(index, 1)
    },
    copyRule(index: number) {
      const clone = clonePaymentRule(this.step4.rules[index])
      this.step4.rules.push(clone)
    },
    setShifts(shifts: Item[]) {
      this.step2.shifts = shifts
    },
    setPositions(positions: Item[]) {
      this.step2.positions = positions
    },
    setFormations(formations: Item[]) {
      this.step2.formations = formations
    },
    setTeams(teams: ItemTeams[]) {
      this.step2.teams = teams
    },
    setDays(days: Item[]) {
      this.step2.days = days
    },
    setFormationRequired(required: boolean) {
      this.step2.formationRequired = required
    },

    setDsAttendancePolicy(data: DSAttendancePolicyData) {
      this.step3 = { ...data }
      this.markAsDirty()
    },

    _buildSchedulePayload() {
      return {
        name: this.step1.scheduleName,
        timezone: this.step1.timezone,
        endDate: this.step1.endDate ? new Date(this.step1.endDate).toISOString() : null,
        healthUnitId: Number(this.step1.healthUnitId),
        proportionalityRule: this.step1.proportionality,
        requiresDoctorPaymentFormation: Boolean(this.step2.formationRequired),
        doctorHandoverAuthorizationMinutes: 0,
        operationalGroup: this.step1.operationalGroup,
        ...buildTeamHandoverPayload(
          this.stepExchangeConfig.ourTeam,
          this.stepExchangeConfig.notOurTeam
        ),
        // Flag off: payload idêntico ao de antes (sem a chave `agendaType`).
        ...(useFeatureFlags().flags.enabledDashboardDemand && {
          agendaType: this.step1.agendaType
        })
      }
    },

    _buildPaymentPolicyPayload() {
      return {
        name: this.step4.name,
        link: {
          startDate: this.step4.startDate ? new Date(this.step4.startDate).toISOString() : null,
          endDate: this.step4.endDate ? new Date(this.step4.endDate).toISOString() : null
        },
        rules: this.step4.rules.map((rule) => ({
          weekdays: rule.days.map((item) => item.value),
          shifts: rule.shifts.map((item) => item.title),
          positions: rule.positions.map((item) => item.title),
          isOurTeamValues: rule.teams.map((item) => item.value.toString()),
          formations: rule.formations.map((item) => item.title),
          paymentTiming: rule.repassMode,
          hourValue: parseFloat(rule.dutyValue)
        }))
      }
    },

    /** Tolerâncias/prazos da política de monitoramento, já normalizados. */
    _buildAttendancePolicySettings() {
      return {
        autoPermanenceOurTeam: Boolean(this.step3.autoPermanenceOurTeam ?? true),
        autoPermanenceNotOurTeam: Boolean(this.step3.autoPermanenceNotOurTeam ?? true),
        checkinLateToleranceMinutes: Number(this.step3.checkinLateToleranceMinutes) || 0,
        checkoutEarlyToleranceMinutes: Number(this.step3.checkoutEarlyToleranceMinutes) || 0,
        submissionDeadlineHours: Number(this.step3.submissionDeadlineHours) || 0,
        deadlineCalculationType: this.step3.deadlineCalculationType || 'CONSECUTIVE_HOURS',
        // Prazo de auditoria (spec 2026-10-01 §6.1): no full-create vai na RAIZ do payload
        // (este objeto é espalhado lá). Sem horas, o cálculo vai `null` junto.
        ...buildAuditDeadlineSettings(
          this.step3.auditDeadlineHours,
          this.step3.auditDeadlineCalculationType
        )
      }
    },

    _buildAttendancePolicyLinks() {
      return (this.step3.links || []).map((link) => ({
        startDate: link.startDate
          ? new Date(link.startDate).toISOString()
          : new Date().toISOString(),
        endDate: link.endDate ? new Date(link.endDate).toISOString() : null
      }))
    },

    /** Folhas de monitoramento — a modalidade EXTERNAL_APP não usa rules. */
    _buildAttendancePolicyRules() {
      if (this.step3.monitoringType === DSAttendanceMonitoringType.EXTERNAL_APP) return []

      return this.step3.rules.map((r, index) => ({
        name: r.name || `Folha de Monitoramento ${index + 1}`,
        shifts: r.shiftIds
          .map((shiftId) => this.step2.shifts.find((s) => s.value === shiftId)?.title)
          .filter((name): name is string => !!name),
        recurrence: String(r.recurrence || ''),
        dayOfMonth: r.dayOfMonth !== null ? Number(r.dayOfMonth) : null,
        daysOfWeek: (r.daysOfWeek || []).map((day) => String(day))
      }))
    },

    _buildAttendancePolicyPayload() {
      return {
        name: this.step3.name,
        requiredCheckOurTeam: Boolean(this.step3.requiredCheckOurTeam),
        requiredCheckNotOurTeam: Boolean(this.step3.requiredCheckNotOurTeam),
        checkMethodOurTeam: this.step3.checkMethodOurTeam ?? null,
        checkMethodNotOurTeam: this.step3.checkMethodNotOurTeam ?? null,
        ...this._buildAttendancePolicySettings(),
        monitoringType: this.step3.monitoringType,
        externalApp: this.step3.externalApp ?? null,
        links: this._buildAttendancePolicyLinks(),
        rules: this._buildAttendancePolicyRules(),
        templates: this.step3.templates.map((template) => ({
          name: template.name || '',
          templateFields: template.templateFields
        }))
      }
    },

    _buildApprovalFlowPayload() {
      if (!this.stepApprovalFlow.enabled) return undefined

      // Choke point do `full-create`: normalizar aqui garante os obrigatórios
      // mesmo que o step tenha sido montado por um caminho que não passou por
      // `createEmptyGroup`/import (state hidratado, edição manual da store).
      return {
        layers: groupToApiLayers(ensureMandatoryRoles(this.stepApprovalFlow.layers[0]))
      }
    },

    /**
     * Vínculo de política de qualificação a replicar (RN-16), no formato que o
     * `full-create` aceita (BUG-PP-06). Vigência: começa hoje e termina junto com
     * a escala (indeterminada quando a escala não tem fim) — a mesma regra que
     * antes ia num POST separado pós-create, que sofria 403 intermitente por
     * corrida de propagação de permissão (outbox→SpiceDB). A janela é da escala
     * nova — o snapshot não carrega vigência, igual aos blocos de pagamento e
     * presença.
     *
     * `startDate` é o hoje do operador (fuso local). `endDate` sai do dia UTC, a
     * mesma convenção que `_buildSchedulePayload` usa para a data final da escala
     * (`toISOString()`): passar a `Date` direto para `toDateOnly` a formataria em
     * fuso local e, para uma data vinda como `...T00:00:00Z`, devolveria o dia
     * ANTERIOR em qualquer fuso negativo.
     */
    _buildQualificationLinkPayload(policy: NamedConfigItem) {
      return {
        policyId: policy.id,
        startDate: toDateOnly(new Date()),
        endDate: this.step1.endDate
          ? toDateOnly(new Date(this.step1.endDate).toISOString())
          : undefined
      }
    },

    /**
     * Só com a flag e com critério (raiz vazia = sem exigência, nada é enviado). Sem `validFrom`:
     * o backend usa hoje em São Paulo (R47.2), não o dia do navegador.
     */
    _buildQualificationPolicyPayload(): CreateScheduleQualificationPolicy | undefined {
      if (!useFeatureFlags().flags.enabledProfessionalDocuments) return undefined
      const { policy } = this.stepQualificationRequirement
      return isPolicyEmpty(policy) ? undefined : { rules: clonePolicy(policy) }
    },

    async createFullSchedule() {
      const qualificationPolicy = this._buildQualificationPolicyPayload()
      const payload: CreateFullScheduleRequest = {
        schedule: this._buildSchedulePayload(),
        positions: this.step2.positions.map((item) => ({ name: item.title })),
        shifts: this.step2.shifts.map((item) => ({ name: item.title })),
        formations: this.step2.formations.map((item) => ({ name: item.title })),
        paymentPolicy: this._buildPaymentPolicyPayload(),
        dsAttendancePolicy: this._buildAttendancePolicyPayload(),
        approvalFlow: this._buildApprovalFlowPayload(),
        ...(this.stepQualification.documental && {
          qualificationDocumentalPolicy: this._buildQualificationLinkPayload(
            this.stepQualification.documental
          )
        }),
        ...(this.stepQualification.transfer && {
          qualificationTransferPolicy: this._buildQualificationLinkPayload(
            this.stepQualification.transfer
          )
        }),
        ...(qualificationPolicy && { qualificationPolicy })
      }

      const healthUnitId = Number(this.step1.healthUnitId)
      this.isSaving = true
      this.loadError = null
      try {
        const created = await Network.schedule.fullCreate(healthUnitId, payload)
        // Criar escala muda o que o usuario pode LER. O gate de render do
        // dashboard (`canReadSchedule`) le o PermissionSet em memoria, que fica
        // memoizado pela sessao inteira do SPA — sem este refetch a escala nova
        // so aparece apos F5.
        //
        // Esperamos a escala ficar legivel em vez de so refazer o set: o backend
        // publica a permissao de forma assincrona e um refetch imediato chega
        // antes dela existir.
        //
        // Sem await de proposito: a escala ja foi criada e a tela nao pode ficar
        // presa ate 30s esperando o pipeline de permissao. O store e reativo, entao
        // a grade passa a renderizar a escala assim que a chave aparece no set.
        void usePermissionsStore()
          .refreshAfterMutation(
            created?.id
              ? {
                  resourceKey: `${AuthorizationResourcesAndSubjects.Schedule}:${created.id}`,
                  permission: AuthorizationPermissions.ScheduleBasicReading
                }
              : undefined
          )
          .catch(() => undefined)
        return created
      } catch (error) {
        console.error('Erro ao criar escala completa:', error)
        this.loadError = getApiErrorMessage(error, CREATE_SCHEDULE_ERROR)
        // Relança: o caller decide o toast. O `loadError` fica para a view que
        // quiser exibir o erro sem depender do toast.
        throw error
      } finally {
        this.isSaving = false
      }
    },

    /**
     * Duplica a configuração de outra escala no wizard inteiro — **todos** os campos,
     * inclusive nome e unidade. O usuário revisa e ajusta antes de salvar.
     *
     * O nome recebe o prefixo `Cópia de ` para as duas escalas não ficarem
     * indistinguíveis na listagem (mesmo padrão dos endpoints de `duplicate` de
     * política no backend). Blocos que a escala de origem não possui resetam o step
     * correspondente, para não deixar resíduo de uma importação anterior.
     */
    async importFromSchedule(scheduleId: number) {
      this.isLoading = true
      this.loadError = null
      try {
        await this._applyImportedConfiguration(scheduleId)
      } catch (error) {
        // `fetchScheduleConfiguration` já registrou a mensagem do fetch; só o que
        // falhar na aplicação do snapshot precisa de mensagem própria aqui.
        if (!this.loadError) {
          this.loadError = getApiErrorMessage(error, IMPORT_CONFIGURATION_ERROR)
        }
        throw error
      } finally {
        this.isLoading = false
      }
    },

    /** Aplicação do snapshot no wizard — separada só para o wrapper de estado acima. */
    async _applyImportedConfiguration(scheduleId: number) {
      const config = await this.fetchScheduleConfiguration(scheduleId)
      const { schedule } = config

      this.step1.scheduleName = `${IMPORTED_NAME_PREFIX}${schedule.name}`
      this.step1.healthUnitId = schedule.healthUnit.id
      this.step1.healthUnitName = schedule.healthUnit.name
      this.step1.timezone = schedule.timezone
      this.step1.endDate = schedule.endDate ? new Date(schedule.endDate) : null
      this.step1.proportionality = schedule.proportionalityRule
      this.step1.operationalGroup = schedule.operationalGroup
      this.step1.teamDoctorReminderEnabled = schedule.teamDoctorReminderEnabled
      this.step1.externalDoctorReminderEnabled = schedule.externalDoctorReminderEnabled

      this.stepExchangeConfig.ourTeam = mapToTeamHandoverConfig(schedule)
      this.stepExchangeConfig.notOurTeam = mapToTeamHandoverConfig({
        allowDoctorStationHandover: schedule.allowDoctorStationHandoverNotOurTeam,
        requiresAdminApproval: schedule.requiresAdminApprovalNotOurTeam,
        handoverRequestHours: schedule.handoverRequestHoursNotOurTeam,
        handoverRequestTiming: schedule.handoverRequestTimingNotOurTeam,
        handoverRequestReference: schedule.handoverRequestReferenceNotOurTeam,
        handoverExpirationHours: schedule.handoverExpirationHoursNotOurTeam,
        handoverExpirationTiming: schedule.handoverExpirationTimingNotOurTeam,
        handoverExpirationReference: schedule.handoverExpirationReferenceNotOurTeam,
        limitConsecutiveHours: schedule.limitConsecutiveHoursNotOurTeam,
        maxConsecutiveHours: schedule.maxConsecutiveHoursNotOurTeam,
        minRestHours: schedule.minRestHoursNotOurTeam,
        consecutiveHoursOurTeamOnly: schedule.consecutiveHoursOurTeamOnlyNotOurTeam
      })

      // `teams` e `days` são catálogos de opções carregados pelo step de pagamento,
      // não configuração importada — por isso são preservados.
      this.step2 = {
        ...this.step2,
        positions: toStepItems(config.positions),
        shifts: toStepItems(config.shifts),
        formations: toStepItems(config.formations),
        formationRequired: schedule.requiresDoctorPaymentFormation
      }

      if (config.dsAttendancePolicy) {
        this.step3 = mapImportedAttendancePolicy(config.dsAttendancePolicy)
      } else {
        this.resetStep3()
      }

      if (config.approvalFlow?.layers?.length) {
        this.stepApprovalFlow = {
          enabled: true,
          layers: [ensureMandatoryRoles(apiLayersToGroup(config.approvalFlow.layers))]
        }
      } else {
        this.resetStepApprovalFlow()
      }

      if (config.paymentPolicy) {
        this.step4 = mapImportedPaymentPolicy(config.paymentPolicy, this.step2)
      } else {
        this.resetStep4()
      }

      // Bloco ausente vira `null`, como nos outros: sem isso uma importação
      // seguinte herdaria a política da anterior.
      this.stepQualification = {
        documental: config.qualificationDocumentalPolicy,
        transfer: config.qualificationTransferPolicy
      }

      // Duplicar leva a Qualificação exigida vigente da origem como cópia independente (D17,
      // R47.3). `null` = origem sem requisitos; chave ausente = flag desligada no backend.
      const sourcePolicy = useFeatureFlags().flags.enabledProfessionalDocuments
        ? config.qualificationPolicy
        : null
      this.stepQualificationRequirement = {
        policy: sourcePolicy ? clonePolicy(sourcePolicy.rules) : emptyPolicy(),
        isValid: true
      }

      this.markAsDirty()
    },

    async fetchScheduleConfiguration(scheduleId: number) {
      try {
        return await Network.schedule.getImportConfiguration(scheduleId)
      } catch (error) {
        console.error('Erro ao buscar configuração da escala na Store:', error)
        this.loadError = getApiErrorMessage(error, IMPORT_CONFIGURATION_ERROR)
        throw error
      }
    },

    async fetchScheduleById(scheduleId: number) {
      this.isLoading = true
      this.loadError = null
      try {
        return await Network.schedule.getById(scheduleId)
      } catch (error) {
        console.error('Erro ao buscar detalhes da escala na Store:', error)
        this.loadError = getApiErrorMessage(error, SCHEDULE_LOAD_ERROR)
        throw error
      } finally {
        this.isLoading = false
      }
    }
  }
})

/**
 * Turnos/posições/formações da escala de origem viram `Item[]` mantendo o **id de
 * origem** como `value`. Isso é obrigatório: `step3.rules[].shiftIds` referencia
 * `step2.shifts[].value`, e o payload de criação envia apenas os `title`, então os
 * ids só precisam ser internamente consistentes.
 */
export function toStepItems(entries: NamedConfigItem[]): Item[] {
  return entries.map((entry) => ({ title: entry.name, value: entry.id }))
}

/**
 * Política de presença importada. `name` e vigência ficam vazios: são da política
 * nova. Se a origem não tiver regras, mantém uma regra em branco para o formulário
 * do step não abrir sem linha nenhuma.
 */
export function mapImportedAttendancePolicy(
  policy: ImportedAttendancePolicy
): DSAttendancePolicyData {
  const rules: DSAttendancePolicyRule[] = policy.rules.map((rule) => ({
    name: rule.name,
    shiftIds: rule.shiftIds,
    recurrence: (rule.recurrence as DSAttendanceRuleRecurrence | null) ?? null,
    dayOfMonth: rule.dayOfMonth,
    daysOfWeek: rule.daysOfWeek
  }))

  return {
    name: '',
    startDate: new Date(),
    endDate: null,
    isIndeterminate: false,
    monitoringType: policy.monitoringType as DSAttendanceMonitoringType,
    externalApp: policy.externalApp,
    requiredCheckOurTeam: policy.requiredCheckOurTeam,
    requiredCheckNotOurTeam: policy.requiredCheckNotOurTeam,
    checkMethodOurTeam: policy.checkMethodOurTeam as CheckMethod | null,
    checkMethodNotOurTeam: policy.checkMethodNotOurTeam as CheckMethod | null,
    autoPermanenceOurTeam: policy.autoPermanenceOurTeam ?? true,
    autoPermanenceNotOurTeam: policy.autoPermanenceNotOurTeam ?? true,
    checkinLateToleranceMinutes: policy.checkinLateToleranceMinutes,
    checkoutEarlyToleranceMinutes: policy.checkoutEarlyToleranceMinutes,
    submissionDeadlineHours: policy.submissionDeadlineHours,
    deadlineCalculationType:
      policy.deadlineCalculationType as DSAttendanceDeadlineCalculationType | null,
    // Prazo de auditoria (spec 2026-10-01): importar traz a configuração inteira da política.
    ...buildAuditDeadlineSettings(
      policy.auditDeadlineHours,
      policy.auditDeadlineCalculationType as DSAttendanceDeadlineCalculationType | null | undefined
    ),
    rules: rules.length
      ? rules
      : [{ name: '', shiftIds: [], recurrence: null, dayOfMonth: null, daysOfWeek: [] }],
    templates: policy.templates.map((template) => ({
      name: template.name,
      templateFields: template.templateFields
    }))
  } as DSAttendancePolicyData
}

/**
 * Política de pagamento importada. As regras vêm com turnos/posições/formações por
 * **nome** (é assim que o full-create as envia), então são recasadas contra os itens
 * já importados no step 2 para recuperar os `value`. Nome que não casa é descartado —
 * enviar um item sem par quebraria a criação.
 *
 * A vigência (`startDate`/`endDate`) não é copiada: é por escala.
 */
export function mapImportedPaymentPolicy(
  policy: ImportedPaymentPolicy,
  step2: ScheduleStep2
): ScheduleStep4 {
  const matchByTitle = (names: string[], catalog: Item[]): Item[] =>
    names
      .map((name) => catalog.find((item) => item.title === name))
      .filter((item): item is Item => !!item)

  return {
    name: policy.name,
    startDate: null,
    endDate: null,
    indefiniteEndDate: false,
    rules: policy.rules.map((rule) => ({
      days: rule.weekdays
        .map((weekday) => weekDays.find((day) => day.value === weekday))
        .filter((day): day is Item => !!day),
      shifts: matchByTitle(rule.shifts, step2.shifts),
      positions: matchByTitle(rule.positions, step2.positions),
      formations: matchByTitle(rule.formations, step2.formations),
      teams: rule.isOurTeamValues
        .map((value) => PAYMENT_TEAM_OPTIONS.find((team) => team.value === value))
        .filter((team): team is ItemTeams => !!team),
      repassMode: rule.paymentTiming === 'IMMEDIATE' ? 'IMMEDIATE' : 'FLOW',
      dutyValue: String(rule.hourValue)
    }))
  }
}

function clonePaymentRule(rule: PaymentRule): PaymentRule {
  return {
    days: rule.days.map((d) => ({ ...d })),
    shifts: rule.shifts.map((s) => ({ ...s })),
    positions: rule.positions.map((p) => ({ ...p })),
    teams: rule.teams.map((t) => ({ ...t })),
    formations: rule.formations.map((f) => ({ ...f })),
    repassMode: rule.repassMode,
    dutyValue: rule.dutyValue,
    antecipeRule: rule.antecipeRule,
    ...(rule.loadedAntecipeRule ? { loadedAntecipeRule: { ...rule.loadedAntecipeRule } } : {})
  }
}
